import { Checkbox } from '@foam/buoy/src/components/Checkbox/Checkbox';
import { cn } from '@foam/buoy/src/lib/utils';

import { getReadableMetricValue } from './utils';

export interface MetricCardProps {
  isDisabled?: boolean;
  isChecked?: boolean;
  onChange?: (v: boolean) => void;
  metricTitle: string;
  metricSubtitle?: string;
  metricValue: number | string | undefined;
  metricTransformValue?: 'percentage' | 'number' | 'string';
}

export const MetricCard: React.FC<MetricCardProps> = ({
  metricTitle,
  metricSubtitle,
  metricValue,
  isDisabled = false,
  isChecked = undefined,
  onChange,
  metricTransformValue = 'number',
}) => {
  const isSelectable = typeof isChecked === 'boolean';
  const Component = isSelectable ? 'label' : 'div';

  return (
    <Component
      className={cn(
        'relative flex flex-col justify-between rounded-12 border bg-card-background-color-default px-6 py-[14px]',
        isSelectable && 'cursor-pointer',
        isChecked ? 'border-blue-500' : 'border-card-border-color-default',
        !isSelectable && 'pointer-events-none',
      )}
    >
      {isSelectable ? (
        <Checkbox
          checked={isChecked}
          onChange={(e) => onChange?.(e.target.checked)}
          disabled={isDisabled}
          aria-label={`Select ${metricTitle}`}
          className="absolute right-2 top-2"
        />
      ) : null}
      <div className="flex flex-col gap-4">
        <div className="pr-10">
          <p
            data-testid="metric-title"
            className={cn(
              'text-16 font-500',
              isDisabled ? 'text-neutral-200' : 'text-neutral-400',
            )}
          >
            {metricTitle}
          </p>
          {metricSubtitle ? (
            <p
              data-testid="metric-subtitle"
              className={cn(
                'text-12 font-400',
                isDisabled ? 'text-neutral-200' : 'text-neutral-400',
              )}
              aria-disabled={isDisabled}
            >
              {metricSubtitle}
            </p>
          ) : null}
        </div>
        <h3
          data-testid="metric-value"
          className={cn(
            'text-32 font-500',
            isDisabled ? 'text-neutral-300' : 'text-neutral-700',
          )}
        >
          {getReadableMetricValue(metricValue, metricTransformValue)}
        </h3>
      </div>
    </Component>
  );
};
